import React, { useMemo, useState, useCallback } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS, bg } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import api from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { toast } from "sonner";

const locales = { "en-US": enUS, "bg": bg };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DnDCalendar = withDragAndDrop(Calendar);

/**
 * Premium monthly/weekly/daily calendar with drag-and-drop.
 * Props: trainings, matches, onSelectEvent, canDrag (coach), onMoved
 */
export default function CalendarGrid({ trainings = [], matches = [], canDrag = false, onSelectEvent, onMoved, height = 640 }) {
  const { lang } = useI18n();
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());

  const events = useMemo(() => {
    const now = Date.now();
    const t = (trainings || []).map((tr) => ({
      id: `t-${tr.id}`,
      raw: tr,
      kind: "training",
      title: tr.title,
      start: new Date(tr.start_at),
      end: new Date(tr.end_at || tr.start_at),
      className: new Date(tr.start_at).getTime() < now ? "event-past" : "",
    }));
    const m = (matches || []).map((mt) => ({
      id: `m-${mt.id}`,
      raw: mt,
      kind: "match",
      title: `⚔ ${mt.title}`,
      start: new Date(mt.start_at),
      end: new Date(new Date(mt.start_at).getTime() + 90 * 60 * 1000),
      className: "event-match",
    }));
    return [...t, ...m];
  }, [trainings, matches]);

  const eventPropGetter = useCallback((event) => ({
    className: event.className || "",
  }), []);

  const onEventDrop = useCallback(async ({ event, start, end }) => {
    if (!canDrag || event.kind !== "training") return;
    try {
      const newStart = new Date(start).toISOString();
      const dur = new Date(event.end).getTime() - new Date(event.start).getTime();
      const newEnd = new Date(new Date(start).getTime() + dur).toISOString();
      await api.put(`/trainings/${event.raw.id}`, {
        title: event.raw.title,
        description: event.raw.description || "",
        location: event.raw.location || "",
        location_lat: event.raw.location_lat,
        location_lng: event.raw.location_lng,
        team: event.raw.team,
        start_at: newStart,
        end_at: newEnd,
      });
      toast.success(lang === "bg" ? "Преместено" : "Moved");
      onMoved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }, [canDrag, lang, onMoved]);

  return (
    <div className="card-sharp p-4" data-testid="calendar-grid">
      <DnDCalendar
        localizer={localizer}
        culture={lang === "bg" ? "bg" : "en-US"}
        events={events}
        view={view}
        onView={setView}
        date={date}
        onNavigate={setDate}
        views={["month", "week", "day", "agenda"]}
        style={{ height }}
        eventPropGetter={eventPropGetter}
        onSelectEvent={(ev) => onSelectEvent?.(ev)}
        draggableAccessor={(ev) => canDrag && ev.kind === "training"}
        onEventDrop={onEventDrop}
        onEventResize={onEventDrop}
        popup
      />
    </div>
  );
}
